import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado "Asignado" de la OT Prueba (tipo_ot=6): espejo de index.php + cargaDetalle.php
// + guarda.php de prueba/ — igual que Adición normal, cataloga equipo Inactivo de
// stock de la sucursal (sin filtrar por cliente, a diferencia de Activar Equipos
// Vendidos), y marca cada equipo asignado con razon='prueba' (ver otPruebaModel.asignarTx).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};
// Input nativo (Box component="input") en vez de TextField para las celdas de la
// tabla de seleccionados — mismo criterio que AsignarEquiposVariosOT.
const inputCompactoSx = {
  fontSize: 11,
  fontFamily: "inherit",
  padding: "3px 6px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 0,
  outline: "none",
  bgcolor: "background.paper",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { bgcolor: (t) => t.palette.action.disabledBackground, color: "text.disabled" },
};

const PAGE_SIZE = 15;
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Modem: "Módems", Chip: "Chips" };
const FAMILIA_LABEL = { RADIO: "Radio", BATERIA: "Batería", MODEM: "Módem", CHIP: "Chip", VARIOS: "Equipo" };

const familiaDe = (t) => ({ Radio: "RADIO", Bateria: "BATERIA", Modem: "MODEM", Chip: "CHIP" }[t] || "VARIOS");

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  if (tipo === "Modem") return "/api/equipos-modem";
  if (tipo === "Chip") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

const AsignarPruebaOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, servicio, esTKR, esAVL, onEnviado, onAsignadosCambiados }) => {
  const { user } = useAuth();
  const [tiposOtros, setTiposOtros] = useState([]);
  const tipos = esTKR ? ["Radio", "Bateria"] : esAVL ? ["Modem", "Chip"] : tiposOtros;
  const [tipo, setTipo] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");

  // { [key]: { tipoEquipo, identificador, grupo, placa, id_ref } }
  const [seleccion, setSeleccion] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [confirmarOpen, setConfirmarOpen] = useState(false);

  const [asignados, setAsignados] = useState([]);
  const [loadingAsignados, setLoadingAsignados] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargarAsignados = () => {
    setLoadingAsignados(true);
    axios.get(`/api/ot-prueba/asignados/${cod_ot}`)
      .then((res) => setAsignados(res.data || []))
      .catch(() => setAsignados([]))
      .finally(() => setLoadingAsignados(false));
  };

  useEffect(() => { cargarAsignados(); }, [cod_ot]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onAsignadosCambiados) onAsignadosCambiados(asignados.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asignados]);

  useEffect(() => {
    if (esTKR || esAVL) return;
    axios.get("/api/equipos-varios/tipos", { params: { servicio } })
      .then((res) => setTiposOtros(res.data || []))
      .catch(() => setTiposOtros([]));
  }, [esTKR, esAVL, servicio]);

  useEffect(() => {
    if (tipos.length && !tipo) setTipo(tipos[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipos.join("|")]);

  // Catálogo de stock Inactivo de la sucursal — sin filtro de cliente (igual que
  // Adición normal / AsignarEquiposVariosOT.jsx), a diferencia de Activar Equipos
  // Vendidos que sí scopea por cod_servicio.
  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    if (!tipo) return;
    setLoadingEquipos(true);
    setError("");
    const params = { estado: "Inactivo", buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE, ciudad: user?.sucursal };
    // /api/equipos-varios necesita `tipo` para distinguir entre los sub-tipos dinámicos
    // (Accesorio POC, CLIP ENRICO, etc.) — los demás endpoints ya son específicos por
    // familia (equipos-radio, equipos-modem, ...) y no lo usan.
    if (!["Radio", "Bateria", "Modem", "Chip"].includes(tipo)) params.tipo = tipo;
    axios
      .get(endpointDeTipo(tipo), { params })
      .then((res) => {
        const data = res.data || {};
        const items = data.items || data.rows || [];
        const identificadorDe = (r) => r.serie ?? r.num_sim ?? r.numero;
        setItems(items.map((r) => ({
          key: `${tipo.toLowerCase()}-${r.ID ?? r.ID_A ?? identificadorDe(r)}`,
          tipoEquipo: tipo,
          identificador: identificadorDe(r),
          modelo: r.modelo,
        })));
        setTotal(Number(data.total ?? 0));
        setPage(Number(data.page || nextPage));
      })
      .catch((err) => {
        console.error("Error al cargar equipo Inactivo", err);
        setError("No se pudo cargar el equipo disponible.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, user?.sucursal]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  const toggleEquipo = (row) => {
    if (seleccion[row.key]) {
      setSeleccion((prev) => {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      });
      return;
    }
    setSeleccion((prev) => ({
      ...prev,
      [row.key]: { tipoEquipo: row.tipoEquipo, identificador: row.identificador, modelo: row.modelo, grupo: "", placa: "", id_ref: "" },
    }));
  };

  const quitarSeleccion = (key) => {
    setSeleccion((prev) => {
      const { [key]: _quitado, ...resto } = prev;
      return resto;
    });
  };

  const setCampo = (key, campo, valor) => {
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: valor } }));
  };

  const seleccionadosEntries = Object.entries(seleccion);
  const seleccionados = Object.values(seleccion);

  const handleConfirmarAsignacion = async () => {
    if (!seleccionados.length) return;
    setEnviando(true);
    setErrores([]);
    const fallidos = [];
    const exitosos = [];
    for (const [key, item] of Object.entries(seleccion)) {
      try {
        await axios.post("/api/ot-prueba/asignar", {
          cod_ot,
          familia: familiaDe(item.tipoEquipo),
          identificador: item.identificador,
          cod_cliente,
          cod_servicio_cliente,
          grupo: item.grupo || "",
          placa: item.placa || "",
          id_ref: item.id_ref || "",
        });
        exitosos.push(key);
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al asignar "${item.identificador}"`);
      }
    }
    if (exitosos.length) {
      setSeleccion((prev) => {
        const next = { ...prev };
        exitosos.forEach((k) => delete next[k]);
        return next;
      });
      cargarPagina(page, buscarAplicado);
      cargarAsignados();
      if (onEnviado) onEnviado();
    }
    setErrores(fallidos);
    setEnviando(false);
    if (!fallidos.length) setConfirmarOpen(false);
  };

  const camposDeTipo = (tipoEquipo) => {
    if (tipoEquipo === "Radio") return ["grupo", "id_ref", "placa"];
    if (tipoEquipo === "Modem") return ["placa", "id_ref"];
    return [];
  };
  const LABEL_CAMPO = { grupo: "Grupo", id_ref: "ID Cliente", placa: "Placa" };
  // Unión de campos de TODOS los tipos con algo seleccionado (no solo el toggle actual):
  // el usuario puede seleccionar Radios, cambiar el toggle a Batería y seleccionar más,
  // así que el diálogo de confirmación puede mostrar filas de más de un tipo a la vez.
  const camposDialogo = [...new Set(seleccionados.flatMap((d) => camposDeTipo(d.tipoEquipo)))];

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      {!loadingAsignados && !!asignados.length && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            Equipos Asignados en esta OT
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Grupo</TableCell>
                  <TableCell sx={headCellSx}>Placa</TableCell>
                  <TableCell sx={headCellSx}>ID Cliente</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asignados.map((r) => (
                  <TableRow key={`${r.familia}-${r.identificador}`} hover>
                    <TableCell>{FAMILIA_LABEL[r.familia] || r.familia}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: r.familia.toLowerCase(), identificador: r.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {r.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{r.modelo}</TableCell>
                    <TableCell>{r.grupo || "—"}</TableCell>
                    <TableCell>{r.placa || "—"}</TableCell>
                    <TableCell>{r.id_ref || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tipos.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <ToggleButtonGroup size="small" exclusive color="primary" value={tipo} onChange={(e, nuevo) => nuevo && setTipo(nuevo)} sx={{ height: 30 }}>
            {tipos.map((t) => (
              <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                {TIPO_LABEL[t] || t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
        <TextField
          size="small"
          label="Buscar"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
          sx={{ minWidth: 160, ...controlSx }}
        />
        <IconButton size="small" onClick={handleBuscar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos Disponibles para Prueba
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 360, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingEquipos ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 2 }}><CircularProgress size={18} /></TableCell></TableRow>
            ) : !items.length ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay {(TIPO_LABEL[tipo] || tipo || "").toLowerCase()} Inactivo(a)s disponibles.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => {
                const checked = Boolean(seleccion[row.key]);
                return (
                  <TableRow key={row.key} hover selected={checked}>
                    <TableCell>
                      <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Página {total ? page : 0} / {total ? totalPages : 0}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingEquipos || page <= 1}
          onClick={() => cargarPagina(page - 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Anterior
        </Button>
        <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingEquipos || page >= totalPages}
          onClick={() => cargarPagina(page + 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Siguiente
        </Button>
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!seleccionados.length}
          onClick={() => setConfirmarOpen(true)}
          sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
        >
          Asignar Equipos Seleccionados
        </Button>
      </Box>

      <Dialog open={confirmarOpen} onClose={() => !enviando && setConfirmarOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar Asignación de Equipos</DialogTitle>
        <DialogContent>
          {!!errores.length && (
            <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
              {errores.map((msg, i) => (<div key={i}>{msg}</div>))}
            </MuiAlert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  {camposDialogo.map((c) => (
                    <TableCell key={c} sx={headCellSx}>{LABEL_CAMPO[c]}</TableCell>
                  ))}
                  <TableCell sx={{ ...headCellSx, width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {seleccionadosEntries.map(([key, datos]) => (
                  <TableRow key={key} hover>
                    <TableCell>{TIPO_LABEL[datos.tipoEquipo] || datos.tipoEquipo}</TableCell>
                    <TableCell>{datos.identificador}</TableCell>
                    {camposDialogo.map((c) => (
                      <TableCell key={c}>
                        {camposDeTipo(datos.tipoEquipo).includes(c) ? (
                          <Box
                            component="input"
                            value={datos[c] ?? ""}
                            onChange={(e) => setCampo(key, c, e.target.value)}
                            sx={{ ...inputCompactoSx, width: 80 }}
                          />
                        ) : "—"}
                      </TableCell>
                    ))}
                    <TableCell>
                      <IconButton size="small" onClick={() => quitarSeleccion(key)} title="Quitar">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmarOpen(false)} disabled={enviando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!seleccionados.length || enviando}
            onClick={handleConfirmarAsignacion}
          >
            {enviando ? <CircularProgress size={16} color="inherit" /> : "Confirmar Asignación"}
          </Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default AsignarPruebaOT;
